import { useEffect, useState } from 'react';
import { Button } from './ui';
import { formatBytes, listMedia, mediaFileUrl, mediaThumbUrl } from '../api/media';

/**
 * A5b.6 — media picker. Chooses one media-library item for a preset config
 * field (e.g. signage `media_id`). Stores the item's id, which is exactly what
 * the server's `compilePreset` reads, so the picker is a view over the existing
 * scalar config — it does not change the compiled layout.
 *
 * A referenced id that is not in the library is not an error here: the publish
 * guard accepts it and warns, so we show it as a plain reference rather than
 * blocking the save.
 */
export function MediaPicker({ id, value, onChange, onBusyChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [browsing, setBrowsing] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    listMedia()
      .then((records) => {
        if (live) {
          setItems(records);
          setError('');
        }
      })
      .catch((cause) => {
        if (live) setError(cause instanceof Error ? cause.message : 'Could not load the media library.');
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  const selected = value ? items.find((item) => item.id === value) ?? null : null;
  const isVideo = (selected?.mime ?? '').startsWith('video/');
  const thumb = selected ? mediaThumbUrl(selected) || mediaFileUrl(selected) : '';

  function choose(item) {
    onChange(item.id);
    setBrowsing(false);
  }

  function clear() {
    onChange('');
    setBrowsing(false);
  }

  useEffect(() => {
    onBusyChange?.(loading);
  }, [loading, onBusyChange]);

  return (
    <div className="media-picker">
      {value ? (
        <figure className="media-card media-picker-selected">
          <div className="media-thumb">
            {isVideo ? (
              <span className="media-video-mark">VIDEO</span>
            ) : thumb ? (
              <img src={thumb} alt={selected?.name ?? 'Selected media'} />
            ) : (
              <span className="media-video-mark">NO PREVIEW</span>
            )}
          </div>
          <figcaption>
            <strong>{selected?.name ?? 'Not in the library'}</strong>
            <span className="mono">{value}</span>
            {selected && (
              <span className="detail-note">
                {selected.mime ?? 'unknown type'} · {formatBytes(selected.size)}
              </span>
            )}
            {!selected && !loading && (
              <span className="detail-note">This id is not in the media library — publish still warns rather than blocks.</span>
            )}
            <div className="row-actions">
              <Button variant="quiet" type="button" onClick={() => setBrowsing((open) => !open)}>
                {browsing ? 'Close' : 'Replace'}
              </Button>
              <Button variant="quiet" type="button" onClick={clear}>
                Remove
              </Button>
            </div>
          </figcaption>
        </figure>
      ) : (
        <Button id={id} variant="quiet" type="button" onClick={() => setBrowsing((open) => !open)}>
          {browsing ? 'Close library' : 'Choose media'}
        </Button>
      )}

      {loading && <p className="detail-note">Loading the media library…</p>}
      {error && (
        <p className="dialog-alert" role="alert">
          <span aria-hidden="true">▲</span>
          {error}
        </p>
      )}

      {browsing && !loading && !error && (
        items.length === 0 ? (
          <p className="detail-note">The media library is empty — upload an image or video on the Media page first.</p>
        ) : (
          <ul className="media-grid media-picker-grid">
            {items.map((item) => {
              const itemThumb = mediaThumbUrl(item) || mediaFileUrl(item);
              const video = (item.mime ?? '').startsWith('video/');
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`media-card media-picker-option${item.id === value ? ' is-selected' : ''}`}
                    onClick={() => choose(item)}
                    aria-pressed={item.id === value}
                  >
                    <span className="media-thumb">
                      {video ? (
                        <span className="media-video-mark">VIDEO</span>
                      ) : itemThumb ? (
                        <img src={itemThumb} alt="" />
                      ) : (
                        <span className="media-video-mark">NO PREVIEW</span>
                      )}
                    </span>
                    <span className="media-picker-name">{item.name ?? item.id}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )
      )}
    </div>
  );
}
